import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Captured at module-load time, before supabase-js' `detectSessionInUrl`
 * has a chance to consume and strip `window.location.hash`. Without this,
 * by the time React mounts and the `useEffect` below reads `location.hash`,
 * the tokens have already been removed and we'd never know an auth callback
 * just landed on a non-portal route.
 */
const INITIAL_HASH =
  typeof window !== "undefined" ? window.location.hash || "" : "";
const INITIAL_PATH =
  typeof window !== "undefined" ? window.location.pathname || "/" : "/";

/**
 * If a Supabase auth callback (magic link / password recovery) lands at any
 * non-portal route with `#access_token=...&type=...` in the hash, forward
 * the user into the portal so a) they actually see a useful screen and
 * b) the supabase-js `detectSessionInUrl` handler can still consume the
 * tokens (we preserve the hash on the navigate call).
 */
export function AuthHashRedirector() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Prefer the live router hash, fall back to the snapshot we took at
    // module load (in case supabase-js already stripped it).
    const hash = location.hash || INITIAL_HASH;
    const pathname = location.pathname || INITIAL_PATH;
    const hasToken = hash.includes("access_token=");
    const hasError = hash.includes("error=") || hash.includes("error_code=");
    if (!hasToken && !hasError) return;

    // Only intercept magic link / recovery / signup / invite callbacks
    // (or matching error responses Supabase appends to the same hash).
    const isAuthCallback =
      /type=(magiclink|recovery|signup|invite)/.test(hash) || hasError;
    if (!isAuthCallback) {
      console.debug("[AuthHashRedirector] hash present but not an auth callback", {
        pathname,
        hashPreview: hash.slice(0, 60),
      });
      return;
    }

    // Already on a portal route - let that page handle it.
    if (pathname.startsWith("/portal")) {
      console.debug("[AuthHashRedirector] already on portal route, skipping", {
        pathname,
      });
      return;
    }

    const callbackType =
      hash.match(/type=([a-z]+)/)?.[1] ?? (hasError ? "error" : "unknown");

    // Recovery (standard password reset) → reset-password page.
    // Magic link / invite / signup / temp-access → change-password page,
    // which itself decides whether to send the user on to dashboard or
    // temp-preview based on their partner profile.
    const target =
      callbackType === "recovery"
        ? "/portal/reset-password"
        : "/portal/change-password";

    console.info("[AuthHashRedirector] forwarding auth callback to portal", {
      from: pathname,
      to: target,
      type: callbackType,
      hasToken,
      hasError,
      usedInitialSnapshot: !location.hash && !!INITIAL_HASH,
    });

    navigate({ pathname: target, hash }, { replace: true });
  }, [location.pathname, location.hash, navigate]);

  return null;
}

export default AuthHashRedirector;
